<template>
  <IonCard class="modern-stepper">
    <IonGrid>
      <IonRow class="justify-center">
        <IonCol v-for="(step, index) in steps" :key="step.key" class="step" :class="{
          completed: index < currentIndex,
          active: index === currentIndex,
          pending: index > currentIndex,
        }">
          <!-- Step Marker -->
          <div class="step-marker">
            <!-- Completed -->
            <IonIcon v-if="index < currentIndex" :icon="checkmarkCircle" class="icon-success" />

            <!-- Active -->
            <div v-else-if="index === currentIndex" class="active-wrapper">
              <div class="pulse"></div>
              <IonIcon :icon="step.icon" class="icon-active" />
            </div>

            <!-- Pending -->
            <IonIcon v-else :icon="step.icon" class="icon-pending" />
          </div>

          <!-- Label -->
          <div class="step-label">
            <span class="label-text">{{ step.label }}</span>
          </div>

          <!-- Connector -->
          <div v-if="index < steps.length - 1" class="connector" :class="{ completed: index < currentIndex }"></div>
        </IonCol>
      </IonRow>
    </IonGrid>
  </IonCard>
</template>

<script setup>
import { ref, computed } from 'vue'
import { IonCard, IonGrid, IonRow, IonCol, IonIcon } from '@ionic/vue'
import {
  checkmarkCircle,
  documentText,
  restaurant,
  checkmarkDone,
  fastFood
} from 'ionicons/icons'

const props = defineProps({
  status: {
    type: String,
    default: 'diterima',
    validator: (v) => ['diterima', 'disiapkan', 'siap'].includes(v),
  },
})

const steps = ref([
  { key: 'diterima', label: 'Pesanan Diterima', icon: documentText },
  { key: 'disiapkan', label: 'Sedang Disiapkan', icon: restaurant },
  { key: 'siap', label: 'Pesanan Siap', icon: checkmarkDone },
  { key: 'taken', label: 'Sudah Diambil', icon: fastFood },
])

const currentIndex = computed(() =>
  steps.value.findIndex((s) => s.key === props.status)
)
</script>

<style scoped>
.modern-stepper {
  border-radius: 20px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
  padding: 24px 12px;
  background: var(--ion-card-background, #fff);
  transition: all 0.3s ease-in-out;
}

.step {
  position: relative;
  text-align: center;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.step-marker {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 72px;
  width: 72px;
  margin-bottom: 12px;
  z-index: 2;
}

/* --- Completed --- */
.icon-success {
  font-size: 44px;
  color: var(--ion-color-success);
  filter: drop-shadow(0 0 6px rgba(16, 185, 129, 0.4));
  transition: all 0.3s ease;
}

/* --- Active --- */
.active-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.icon-active {
  font-size: 40px;
  color: var(--ion-color-primary);
  z-index: 2;
}

.pulse {
  position: absolute;
  width: 82px;
  height: 82px;
  border-radius: 50%;
  background: rgba(var(--ion-color-primary-rgb), 0.15);
  animation: pulseAnim 2s infinite;
  z-index: 1;
}

@keyframes pulseAnim {
  0% {
    transform: scale(0.9);
    opacity: 0.7;
  }

  50% {
    transform: scale(1.2);
    opacity: 0.3;
  }

  100% {
    transform: scale(0.9);
    opacity: 0.7;
  }
}

/* --- Pending --- */
.icon-pending {
  font-size: 38px;
  color: var(--ion-color-medium);
  opacity: 0.6;
}

/* --- Label --- */
.step-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--ion-color-dark);
  transition: color 0.3s;
}

.completed .step-label {
  color: var(--ion-color-success);
}

.active .step-label {
  color: var(--ion-color-primary);
}

.pending .step-label {
  color: var(--ion-color-medium);
}

/* --- Connector (Tengah Icon & Lebih Tebal) --- */
.connector {
  position: absolute;
  top: 30%;
  left: calc(50% + 36px);
  width: calc(100% - 72px);
  height: 5px;
  transform: translateY(-50%);
  background: var(--ion-color-medium);
  opacity: 0.3;
  border-radius: 4px;
  z-index: 1;
}

.connector.completed {
  background: linear-gradient(90deg,
      var(--ion-color-success) 0%,
      var(--ion-color-success-tint) 100%);
  opacity: 0.9;
  box-shadow: 0 0 6px rgba(34, 197, 94, 0.4);
}

/* Responsive */
@media (max-width: 600px) {
  .step-marker {
    height: 56px;
    width: 56px;
  }

  .connector {
    left: calc(50% + 28px);
    width: calc(100% - 56px);
    height: 4px;
  }

  .icon-active,
  .icon-success,
  .icon-pending {
    font-size: 30px;
  }
}
</style>
